import { useTranslationStore } from '@src/stores-v2/StoreContext';
import { useCallback, useEffect, useState } from 'react';

import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * PLAT-11650 — which translated phrase is being edited IN PLACE (the click-and-
 * type field), kept apart from `usePhraseEditing`, which stays the popover /
 * context-menu state machine. Identity is `blockId + lead` (render position),
 * not the phrase object, so a store rewrite mid-typing — the debounced save
 * landing, another reviewer's change — never unmounts the field.
 */
export interface InlinePhraseEditState {
  /** The carried phrase record (`originalIndices`, `originalElementIds`). */
  phrase: TranslationPhrase;
  blockId: string;
  /** The rendered phrase's lead index — the render key. */
  lead: number;
  /** Character offset the caret opens at; clamped by the field. */
  caretOffset: number;
  /** The phrase span, for the bubble to anchor under. */
  anchorElement: HTMLElement;
}

export interface UseInlinePhraseEditResult {
  inlineEdit: InlinePhraseEditState | null;
  /**
   * The text the field opens with: a pending (unsaved) edit wins over the
   * stored value — the same resolution the popover uses.
   */
  inlineCurrentValue: string;
  openInlineEdit: (state: InlinePhraseEditState) => void;
  cancelInlineEdit: () => void;
}

export function useInlinePhraseEdit(
  language: string,
  editable: boolean,
): UseInlinePhraseEditResult {
  const translationStore = useTranslationStore();
  const [inlineEdit, setInlineEdit] = useState<InlinePhraseEditState | null>(
    null,
  );

  useEffect(() => {
    setInlineEdit(null);
  }, [language]);
  useEffect(() => {
    if (!editable) setInlineEdit(null);
  }, [editable]);

  const openInlineEdit = useCallback((state: InlinePhraseEditState) => {
    setInlineEdit(state);
  }, []);
  const cancelInlineEdit = useCallback(() => setInlineEdit(null), []);

  const inlineCurrentValue = inlineEdit
    ? (translationStore.getTranslatedPhraseByElementIds(
        language,
        inlineEdit.phrase.originalElementIds,
      ) ?? inlineEdit.phrase.translatedValue)
    : '';

  return { inlineEdit, inlineCurrentValue, openInlineEdit, cancelInlineEdit };
}
